import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import { useResolvedChart } from 'features/timeSeries'
import type { PointAnnotation } from 'features/timeSeries/components/pointTool/usePointAnnotationTool'
import type { Ruler } from 'features/timeSeries/components/rulerTool/useRulerTool'
import {
  loadPointAnnotationsFromChart,
  loadRulerConfigsFromChart,
} from 'features/timeSeries/utils/chartConfigLoaders'
import {
  chartLocalStateKey,
  clearChartLocalState,
} from 'features/timeSeries/utils/chartLocalState'
import { isUnsavedNewChart } from 'features/timeSeries/utils/chartSavePrompt'
import {
  buildChartPatchData,
  compareSourceConfigs,
  serializePointAnnotationConfigs,
  serializeRulerConfigs,
} from 'features/timeSeries/utils/chartSerialization'
import { serializeDataProcessingConfigs } from 'features/timeSeries/utils/dataProcessingSerialization'
import { MutableRefObject, useCallback, useMemo, useState } from 'react'
import {
  ChartInteractionMode,
  DataProcessingConfig,
  DateConfig,
  RolodexEntryType,
  SourceConfig,
  YAxisMode,
} from 'types'

type UseChartSaveArgs = {
  chartID: string
  name?: string
  description?: string
  selectedSourceConfigs: SourceConfig[]
  yAxisMode: YAxisMode
  dateConfig: DateConfig
  /**
   * Threshold filters, smoothing and regressions, flattened by
   * `buildDataProcessingConfigsFromState`.
   *
   * Optional so callers that do not track this state keep their current
   * behaviour: when omitted, `data_processing_configs` is left out of the patch
   * entirely rather than written as an empty array, so an existing chart's
   * configs are not clobbered by a caller that cannot see them.
   */
  dataProcessingConfigs?: DataProcessingConfig[]
  /**
   * Ruler / point-annotation / open-tool state.
   *
   * Optional for the same reason as `dataProcessingConfigs`: when omitted the
   * corresponding key is left out of the patch entirely rather than written as
   * an empty array, so a caller that cannot see this state does not clobber it.
   */
  rulers?: Ruler[]
  pointAnnotations?: PointAnnotation[]
  interactionMode?: ChartInteractionMode
  tags?: Record<string, string | null | undefined>
  derivedValueIdsToDeleteRef?: MutableRefObject<Set<string>>
  onChartSaved?: (chartID: string, isNewChart: boolean) => Promise<void>
  /** Skip resolving the chart by ID; treat as a brand-new chart the whole time. */
  transient?: boolean
}

/** Stable key for a config regardless of array order. */
const dataProcessingConfigKey = (config: DataProcessingConfig): string =>
  config.type === 'regression'
    ? `regression:${config.id}`
    : `${config.type}:${config.sensorId}`

/**
 * Compare by serialized form: live state and the schema's `.transform()` output
 * are both camelCase but need not agree on key *order*, which plain
 * `JSON.stringify` is sensitive to. Serializing first gives both sides an
 * identical, canonical key order.
 */
const compareDataProcessingConfigs = (
  a: DataProcessingConfig[],
  b: DataProcessingConfig[]
): boolean => {
  if (a.length !== b.length) return false

  const serialize = (configs: DataProcessingConfig[]) =>
    new Map(
      configs.map((config) => [
        dataProcessingConfigKey(config),
        JSON.stringify(serializeDataProcessingConfigs([config])[0]),
      ])
    )

  const serializedA = serialize(a)
  const serializedB = serialize(b)

  if (serializedA.size !== serializedB.size) return false

  for (const [key, value] of serializedA) {
    if (serializedB.get(key) !== value) return false
  }
  return true
}

/**
 * Compare rulers by their *serialized* form, which is what makes the pixel
 * fields invisible here: `startPixelOnOverlay`/`endPixelOnOverlay` are recomputed
 * from the x-domain on every zoom/pan (RulerOverlay's `setScale` hook calls
 * `updateRulerPixels`), so comparing the live shape would report "unsaved
 * changes" after a pan that changed nothing durable.
 */
const compareRulers = (a: Ruler[], b: Ruler[]): boolean =>
  JSON.stringify(serializeRulerConfigs(a)) ===
  JSON.stringify(serializeRulerConfigs(b))

const comparePointAnnotations = (
  a: PointAnnotation[],
  b: PointAnnotation[]
): boolean =>
  JSON.stringify(serializePointAnnotationConfigs(a)) ===
  JSON.stringify(serializePointAnnotationConfigs(b))

const compareDateConfigs = (a: DateConfig, b: DateConfig): boolean => {
  if (a.mode !== b.mode) return false

  if (a.mode === 'absolute' && b.mode === 'absolute') {
    return (
      a.startDate.getTime() === b.startDate.getTime() &&
      a.endDate.getTime() === b.endDate.getTime()
    )
  }

  if (a.mode === 'relative' && b.mode === 'relative') {
    const startEqual =
      a.startDateLookback.value === b.startDateLookback.value &&
      a.startDateLookback.unit === b.startDateLookback.unit

    const endEqual =
      JSON.stringify(a.endDateLookback) === JSON.stringify(b.endDateLookback)

    const cadenceEqual = a.updateCadenceSeconds === b.updateCadenceSeconds

    return startEqual && endEqual && cadenceEqual
  }

  return false
}

export const useChartSave = ({
  chartID,
  name,
  description,
  selectedSourceConfigs,
  yAxisMode,
  dateConfig,
  dataProcessingConfigs,
  rulers,
  pointAnnotations,
  interactionMode,
  tags,
  derivedValueIdsToDeleteRef,
  onChartSaved,
  transient = false,
}: UseChartSaveArgs) => {
  const { rolodexPatch, rolodexDelete } = useRolodexEdit()
  const { successNotification, errorNotification } = useGeckoNotifications()

  /**
   * A transient chart stops being transient the moment its first save lands:
   * the slug now names a real entry, so resolving it lets `hasUnsavedChanges`
   * converge to false and subsequent saves behave as updates. Without this an
   * embedded/ad-hoc chart would show the unsaved dot forever after saving.
   */
  const [promotedChartID, setPromotedChartID] = useState<string | null>(null)

  const validChartID =
    chartID &&
    chartID.trim() !== '' &&
    (!transient || promotedChartID === chartID)
      ? chartID
      : undefined

  /**
   * True only while this chart has never been persisted: a transient slug whose
   * first save has not yet promoted it into a real entry. Saved charts (never
   * transient) and already-promoted transient charts are false, so their
   * re-saves stay silent. This is the gate the Save button uses to decide
   * whether to prompt the user for a name before the first save.
   */
  const isNewChart = isUnsavedNewChart({ transient, chartID, promotedChartID })
  const { chart: savedChart, mutate: mutateChart } =
    useResolvedChart(validChartID)

  const { entries, forceRefresh: refreshEntry } = useRolodexEntries(
    validChartID
      ? { ids: [validChartID], type: [RolodexEntryType.CHART] }
      : undefined
  )

  const lastSavedAt = entries?.[0]?.updated_at
    ? new Date(entries[0].updated_at)
    : null

  const nextDataCamel = useMemo(() => {
    return {
      name,
      description,
      sourceConfigs: selectedSourceConfigs,
      yAxisMode,
      dateConfig,
      dataProcessingConfigs,
      rulers,
      pointAnnotations,
      interactionMode,
    }
  }, [
    name,
    description,
    selectedSourceConfigs,
    yAxisMode,
    dateConfig,
    dataProcessingConfigs,
    rulers,
    pointAnnotations,
    interactionMode,
  ])

  const hasUnsavedChanges = useMemo(() => {
    if (!savedChart) {
      return (
        nextDataCamel.sourceConfigs.length > 0 ||
        Boolean(nextDataCamel.name?.trim())
      )
    }

    if (
      derivedValueIdsToDeleteRef &&
      derivedValueIdsToDeleteRef.current.size > 0
    ) {
      return true
    }

    const nameMatch = (nextDataCamel.name ?? '') === (savedChart.name ?? '')
    const descriptionMatch =
      (nextDataCamel.description ?? '') === (savedChart.description ?? '')
    const sourceConfigsMatch = compareSourceConfigs(
      nextDataCamel.sourceConfigs,
      savedChart.sourceConfigs
    )

    const yAxisModeMatch = nextDataCamel.yAxisMode === savedChart.yAxisMode

    const dateConfigMatch = compareDateConfigs(
      nextDataCamel.dateConfig,
      savedChart.dateConfig
    )

    // Undefined means "this caller does not track processing configs", which is
    // not a change — only compare when the caller actually supplied them.
    const dataProcessingMatch =
      nextDataCamel.dataProcessingConfigs === undefined ||
      compareDataProcessingConfigs(
        nextDataCamel.dataProcessingConfigs,
        savedChart.dataProcessingConfigs ?? []
      )

    // Undefined means "this caller does not track tool state", which is not a
    // change — only compare when the caller actually supplied it.
    const rulersMatch =
      nextDataCamel.rulers === undefined ||
      compareRulers(nextDataCamel.rulers, loadRulerConfigsFromChart(savedChart))

    const pointAnnotationsMatch =
      nextDataCamel.pointAnnotations === undefined ||
      comparePointAnnotations(
        nextDataCamel.pointAnnotations,
        loadPointAnnotationsFromChart(savedChart)
      )

    // A chart saved before `interaction_mode` existed reads back undefined;
    // treat that as the default 'none' so merely loading it is not a change.
    const interactionModeMatch =
      nextDataCamel.interactionMode === undefined ||
      nextDataCamel.interactionMode === (savedChart.interactionMode ?? 'none')

    return (
      !nameMatch ||
      !descriptionMatch ||
      !sourceConfigsMatch ||
      !yAxisModeMatch ||
      !dateConfigMatch ||
      !dataProcessingMatch ||
      !rulersMatch ||
      !pointAnnotationsMatch ||
      !interactionModeMatch
    )
  }, [nextDataCamel, savedChart, derivedValueIdsToDeleteRef])

  // A narrower signal than `hasUnsavedChanges`: true only when the ruler/point
  // annotations differ from what was last saved. Drives the dedicated "unsaved
  // annotations" indicator by the Save button.
  const hasUnsavedAnnotationChanges = useMemo(() => {
    if (
      nextDataCamel.rulers === undefined &&
      nextDataCamel.pointAnnotations === undefined
    ) {
      return false
    }
    if (!savedChart) {
      return (
        (nextDataCamel.rulers?.length ?? 0) > 0 ||
        (nextDataCamel.pointAnnotations?.length ?? 0) > 0
      )
    }
    const rulersMatch =
      nextDataCamel.rulers === undefined ||
      compareRulers(nextDataCamel.rulers, loadRulerConfigsFromChart(savedChart))
    const pointAnnotationsMatch =
      nextDataCamel.pointAnnotations === undefined ||
      comparePointAnnotations(
        nextDataCamel.pointAnnotations,
        loadPointAnnotationsFromChart(savedChart)
      )
    return !rulersMatch || !pointAnnotationsMatch
  }, [nextDataCamel, savedChart])

  const saveChart = useCallback(
    async (nameOverride?: string) => {
      if (!chartID || chartID.trim() === '') {
        const error = 'Cannot save chart: Invalid chart ID'
        errorNotification(error)
        return
      }

      // The name prompt confirms with the entered name synchronously; passing it
      // here avoids a save that races the `setChartName` state flush (which never
      // arrives when the user accepts the pre-filled default unchanged).
      const patchEntry = {
        id: chartID,
        type: RolodexEntryType.CHART,
        data: buildChartPatchData(
          nameOverride === undefined
            ? nextDataCamel
            : { ...nextDataCamel, name: nameOverride }
        ),
        tags: tags ?? {},
      }

      try {
        // Save the chart
        await rolodexPatch([patchEntry])

        // First save of a transient chart: the slug is now a real entry.
        if (transient) {
          setPromotedChartID(chartID)
        }

        // ANY successful save makes the rolodex entry the baseline for this
        // chart, on every surface. Surfaces that persist local state rewrite
        // their draft to the saved payload in `onChartSaved` (below, after this
        // clear); every other surface — e.g. the embedded doc-viewer chart —
        // must not leave an older draft behind to mask this save on the
        // standalone `/process-data/chart/<id>` page. Clearing is correct
        // there: saved == baseline, so the standalone page hydrates fresh.
        const siteTag = tags?.site
        if (typeof siteTag === 'string' && siteTag) {
          clearChartLocalState(chartLocalStateKey(siteTag, chartID))
        }

        // Delete derived values that were removed by the user
        if (
          derivedValueIdsToDeleteRef &&
          derivedValueIdsToDeleteRef.current.size > 0
        ) {
          await Promise.all(
            Array.from(derivedValueIdsToDeleteRef.current).map((id) =>
              rolodexDelete(id, false)
            )
          )
          derivedValueIdsToDeleteRef.current.clear()
        }

        if (onChartSaved) {
          await onChartSaved(chartID, isNewChart)
        }

        await mutateChart()
        await refreshEntry()

        successNotification('Chart saved successfully!')
      } catch (err) {
        console.error('Chart save failed', err)
        errorNotification(`Failed to save chart: ${err}`)
        throw err
      }
    },
    [
      chartID,
      nextDataCamel,
      rolodexPatch,
      rolodexDelete,
      tags,
      derivedValueIdsToDeleteRef,
      mutateChart,
      refreshEntry,
      successNotification,
      errorNotification,
      isNewChart,
      onChartSaved,
      transient,
    ]
  )

  return {
    saveChart,
    hasUnsavedChanges,
    hasUnsavedAnnotationChanges,
    lastSavedAt,
    isNewChart,
  }
}
